import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import {
  DateRange,
  extractNumber,
  extractNumberArr,
  HxCurrencyPipe,
  HxMoneyFormatPipe,
  HxReportService,
  RfmClientAnalysisModel,
  RfmUtmAnalysisModel,
  SourceSystem,
  toRouterQueryParams
} from 'hx-services';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { DatePipe, NgClass, NgFor, NgIf } from '@angular/common';
import { HxEnumSelectComponent, HxLoaderComponent } from 'hx-component';
import { FormsModule } from '@angular/forms';
import { NgbPagination } from '@ng-bootstrap/ng-bootstrap';

@Component({
    selector: 'app-rfm-analysis-segment',
    templateUrl: './rfm-analysis-segment.component.html',
    styleUrls: ['./rfm-analysis-segment.component.css'],
    imports: [NgIf, HxEnumSelectComponent, FormsModule, NgFor, NgClass, HxLoaderComponent, NgbPagination, DatePipe, TranslocoPipe, HxCurrencyPipe, HxMoneyFormatPipe]
})
export class RfmAnalysisSegmentComponent implements OnInit, OnDestroy {
  SourceSystem = SourceSystem;
  sourceSystemExclude = [SourceSystem.SUPPLIER, SourceSystem.VANILLA, SourceSystem.KIOSK, SourceSystem.QUEUE, SourceSystem.DELIVERY, SourceSystem.WEBKASSA, SourceSystem.CONFECTIONARY];
  isloading = {
    rfm: false,
  };
  params: {
    page: number,
    limit: number,
    date?: DateRange,
    maxFrequency?: number,
    maxMonetary?: number,
    storeIds?: number[],
    segments?: string[],
    source?: SourceSystem,
  }= { page: 1, limit: 20 };
  list: RfmClientAnalysisModel[] = [];
  utmList: RfmUtmAnalysisModel[] = [];
  pagination = {
    allItemCount: 0
  };
  private sub!: Subscription;

  constructor(
    private reportService: HxReportService,
    private aRoute: ActivatedRoute,
    private router: Router,
    private zone: NgZone,
    private tr: TranslocoService
  ) {
  }

  browserOnly(fn: () => void) {
    this.zone.runOutsideAngular(() => fn());
  }

  ngOnInit(): void {
    this.sub = this.aRoute.queryParamMap.subscribe(async paramMap => {
      this.params.date = {from: paramMap.get('dateFrom'), to: paramMap.get('dateTo')};
      this.params.maxMonetary = extractNumber('maxMonetary', paramMap) ?? 10000;
      this.params.maxFrequency = extractNumber('maxFrequency', paramMap) ?? 10;
      this.params.storeIds = extractNumberArr('storeIds', paramMap) ?? [];
      this.params.segments = paramMap.get('segments')?.split(',') ?? [];
      this.params.limit = extractNumber('limit', paramMap) ?? 20;
      this.params.page = extractNumber('page', paramMap) ?? 1;
      this.params.source = paramMap.get('source') ? paramMap.get('source') as SourceSystem : undefined;

      this.load();
      this.createPie();
    });
  }

  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  pageChanged(event: number) {
    this.params.page = event;
    this.navigate();
  }

  applyFilters() {
    this.params.page = 1;
    this.navigate();
  }

  private navigate() {
    this.router.navigate(['./'], {
      queryParams: toRouterQueryParams(this.params),
      relativeTo: this.aRoute
    });
  }

  private async load() {
    this.isloading.rfm = true;
    try {
      const response = await this.reportService.getRfmClientAnalysis({
        fromDate: this.params.date.from,
        toDate: this.params.date.to,
        storeIds: this.params.storeIds,
        maxFrequency: this.params.maxFrequency,
        maxMonetary: this.params.maxMonetary,
        segments: this.params.segments,
        source: this.params.source,
        page: this.params.page,
        limit: this.params.limit
      });
      if (response.count > 0) {
        this.list = response.list;
        this.pagination.allItemCount = response.count;
      } else {
        this.list = [];
        this.pagination.allItemCount = 0;
      }
    } finally {
      this.isloading.rfm = false;
    }
  }

  private async createPie() {
    this.utmList = [];
    this.utmList = await this.reportService.getRfmUtmAnalysis({
      fromDate: this.params.date.from,
      toDate: this.params.date.to,
      storeIds: this.params.storeIds,
      maxFrequency: this.params.maxFrequency,
      maxMonetary: this.params.maxMonetary,
      segments: this.params.segments
    });

    this.browserOnly(() => {
      Promise.all([
        import('@amcharts/amcharts4/core'),
        import('@amcharts/amcharts4/charts'),
        import('@amcharts/amcharts4/themes/animated').then(res => res.default)
      ]).then(([am4core, am4charts, am4themesAnimated]) => {
        am4core.useTheme(am4themesAnimated);

        let chart = am4core.create('chartdiv', am4charts.PieChart);

        this.utmList.forEach(r => {
          if (!r.utmSource) {
            r.utmSource = this.tr.translate('rfm-analysis.other');
          }
        });

        // Данные
        chart.data = this.utmList;

        let pieSeries = chart.series.push(new am4charts.PieSeries());
        pieSeries.dataFields.value = 'count';
        pieSeries.dataFields.category = 'utmSource';

        pieSeries.slices.template.tooltipText = '{utmSource}: {count}';

        chart.legend = new am4charts.Legend();
        chart.legend.position = 'top';

      }).catch((e) => {
        console.error('Error when creating chart', e);
      });
    });
  }
}
